import React, {Component} from "react"
import PropTypes from "prop-types"
import {isNull, get, map, flatten, uniq, differenceWith} from "lodash"

import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import categoriesService from "@irati/deals/services/dealCategory"
import {updatePrivateUserMetadata, getPrivateUserMetadata} from "@irati/user-metadata/services/UserMetadata"
import {UserTracking as tracking, UserFlowIds} from "@irati-tracking/onboarding"
import Loading from "@irati/ui/src/Loading"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {hangPromise} from "@irati/utils/promise"
import notify from "@irati/utils/notify"
import Card from "@irati/ui/src/Layout/Card"
import {H1, Body} from "@irati/ui/src/Typography"
import Spacing from "@irati/ui/src/Spacing"
import {RouterLink} from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {Strong} from "@irati/ui/src/Labels"
import ErrorText from "@irati/ui/src/ErrorText"
import SuccessText from "@irati/ui/src/SuccessText"
import {Tag, UserMetadata} from "@irati/types"
import {DEFAULT_CONFIG} from "@irati/user-metadata/constants/defaultPushConfig"
import UnavailableCategoryModal from "@irati-web/perks/src/components/CustomCategories/UnavailableCategoryModal"
import CustomisationCategory from "../../components/CustomisationCategory"
import OnLeaveCustomisation from "../../components/OnLeaveCustomisation"

const MAX_FAVOURITE_CATEGORIES = 7

interface Props extends InjectIntl, WithAuth {}

type CustomisationTag = Pick<Tag, "uuid" | "name">

interface State {
  load: Promise<any>,
  saving: Promise<any> | null,
  categories?: Tag[],
  tags?: CustomisationTag[],
  metadata: UserMetadata | null,
  currentSelection: CustomisationTag[],
  showError: boolean | null,
  saved: boolean,
  unavailableCategories?: Tag[],
  showUnavailableCategoryModal: boolean
}

const getCustomisationTag = ({uuid, name}: Tag) => { return {uuid, name}}

const matchTagByUuid = (matchUuid: string) => ({uuid}: CustomisationTag) => matchUuid === uuid

class CategoriesCustomisation extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      load: hangPromise(),
      saving: null,
      metadata: null,
      currentSelection: [],
      showError: false,
      saved: false,
      showUnavailableCategoryModal: true
    }
  }

  componentDidMount() {
    const {
      auth: {
        user: {uuid}
      }
    } = this.props
    const load = Promise.all([categoriesService.listForCurrentPackage(), getPrivateUserMetadata(uuid, {fetchPolicy: "no-cache"})])
      .then(([categories, metadata]) => {
        const selectableCategories = categories.filter(({type, isGeolocationRelated}) => isNull(type) && !isGeolocationRelated)
        const selectableTags = uniq(
          flatten(map(selectableCategories, "tags"))
            .filter(({filterable}) => filterable === true).map(getCustomisationTag)
        )

        const favouriteCategories = metadata?.config?.favouriteCategories || []
        const startingSelection = favouriteCategories.map(getCustomisationTag)
          .filter(({uuid: favouriteUuid}) => {
            const categoriesFilter = matchTagByUuid(favouriteUuid)
            return selectableTags.find(categoriesFilter) || selectableCategories.find(categoriesFilter)
          })
        const unavailableCategories = differenceWith(favouriteCategories, startingSelection,
          (({uuid: favouriteUuid}: CustomisationTag, {uuid: validUuid}: CustomisationTag) => favouriteUuid === validUuid))

        this.setState({
          categories: selectableCategories,
          tags: selectableTags,
          metadata,
          currentSelection: startingSelection,
          unavailableCategories
        })
      })
    this.setState({load})
  }

  getMetadataConfig = (metadata: UserMetadata | null) => {
    return metadata?.config || DEFAULT_CONFIG
  }

  toggleSelection = (id: string, name: string) => {
    const {currentSelection} = this.state
    let updatedSelection
    if (currentSelection.find(({uuid}) => uuid === id)) {
      updatedSelection = currentSelection.filter(({uuid}) => uuid !== id)
      tracking.categoryDeselected({
        category_id: id,
        category_name: name,
        flow: UserFlowIds.CategoriesCustomisation
      })
    } else {
      updatedSelection = currentSelection.concat({uuid: id, name})
      tracking.categorySelected({
        category_id: id,
        category_name: name,
        flow: UserFlowIds.CategoriesCustomisation
      })
    }
    this.setState({
      showError: updatedSelection.length > MAX_FAVOURITE_CATEGORIES,
      currentSelection: updatedSelection
    })
  }

  hasChanged = () => {
    const {metadata = {}, currentSelection} = this.state
    const favouriteCategoryIds = get(metadata, "config.favouriteCategoryIds")
    return map(currentSelection, "uuid").sort().join("") !== (favouriteCategoryIds || []).sort().join("")
  }

  saveChanges = () => {
    const {
      auth: {
        user: {uuid: userUuid}
      },
      formatMessage
    } = this.props
    const {metadata, currentSelection, tags} = this.state
    const favouriteCategoryIds = get(metadata, "config.favouriteCategoryIds") || []
    const favouriteCategoryNames = favouriteCategoryIds.map((id: string) => tags?.find(({uuid}) => uuid === id)?.name)
    const currentSelectedIds = map(currentSelection, "uuid")

    tracking.categoriesSaved({
      category_ids: currentSelectedIds,
      category_names: map(currentSelection, "name"),
      previous_category_ids: favouriteCategoryIds,
      previous_category_names: favouriteCategoryNames,
      flow: UserFlowIds.CategoriesCustomisation
    })

    const saving = updatePrivateUserMetadata({
      ...metadata,
      config: {
        ...this.getMetadataConfig(metadata),
        favouriteCategoryIds: currentSelectedIds
      },
      uuid: userUuid
    }).then((data: UserMetadata) => {
      this.setState({metadata: data, saving: null, showError: false, saved: true})
      setTimeout(() => {
        this.setState({
          saved: false
        })
      }, 5000)
    }).catch(() => {
      notify.error({message: formatMessage({id: "account.customisation.interests.error"})})
      this.setState({saving: null})
    })
    this.setState({saving})
  }

  resetChanges = () => {
    const {currentSelection} = this.state

    tracking.categoriesReset({
      reset_category_ids: map(currentSelection, "uuid"),
      reset_category_names: map(currentSelection, "name"),
      flow: UserFlowIds.CategoriesCustomisation
    })
    this.setState({
      showError: false,
      currentSelection: []
    })
  }

  abandonChanges = () => {
    const {metadata, currentSelection, tags} = this.state
    const favouriteCategoryIds = get(metadata, "config.favouriteCategoryIds") || []
    const favouriteCategoryNames = favouriteCategoryIds.map((id: string) => tags?.find(({uuid}) => uuid === id)?.name)

    tracking.categoryChangesAbandoned({
      abandoned_category_ids: map(currentSelection, "uuid"),
      abandoned_category_names: map(currentSelection, "name"),
      reset_to_category_ids: favouriteCategoryIds,
      reset_to_category_names: favouriteCategoryNames,
      flow: UserFlowIds.CategoriesCustomisation
    })
  }

  removeUnavailableCategories = () => {
    const {
      auth: {user}
    } = this.props
    const {uuid: userUuid} = user
    const {metadata, unavailableCategories} = this.state
    const unavailableCategoryIds = map(unavailableCategories, "uuid")

    const config = metadata?.config || DEFAULT_CONFIG
    const newFavouriteCategories = get(metadata, "config.favouriteCategories", [])
      .filter(({uuid}: Tag) => !unavailableCategoryIds.includes(uuid))

    return updatePrivateUserMetadata({
      ...metadata,
      config: {
        ...config,
        favouriteCategoryIds: map(newFavouriteCategories, "uuid")
      },
      uuid: userUuid
    })
  }

  hideModal = () => {
    const {unavailableCategories} = this.state
    tracking.categoryRemovedModalDismissed({
      category_ids: map(unavailableCategories, "uuid"),
      category_names: map(unavailableCategories, "name"),
      flow: UserFlowIds.CategoriesCustomisation
    })
    this.removeUnavailableCategories()
    this.setState({showUnavailableCategoryModal: false})
  }

  render() {
    const {apps} = this.context
    const {load, saving, categories, currentSelection, showError, saved, unavailableCategories, showUnavailableCategoryModal} = this.state

    const selectedDisplay = `${currentSelection.length}/${MAX_FAVOURITE_CATEGORIES} `
    const SelectedSummaryComponent = showError ? ErrorText : Flex
    const hasChanged = this.hasChanged()

    const header = (
      <Strong>
        <FormattedMessage id="account.customisation.interests.rowHeader" />
      </Strong>
    )
    const link = (
      <RouterLink to="/discover" app={apps.PERKS}>
        <FormattedMessage id="account.customisation.interests.linkText" />
      </RouterLink>
    )

    return (
      <>
        <Card tiny noBorder>
          <H1 marginBottom={2}>
            <FormattedMessage id="account.customisation.interests.header" />
          </H1>
          <Body>
            <FormattedMessage
              id="account.customisation.interests.description"
              values={{
                header,
                link
              }} />
          </Body>
          <Loading load={load}>
            <Spacing marginTop={2}>
              <SelectedSummaryComponent>
                {selectedDisplay}&nbsp;
                <FormattedMessage id="account.customisation.interests.selectedLabel" />
              </SelectedSummaryComponent>
            </Spacing>
            {categories &&
              categories.map((category) =>
                <CustomisationCategory
                  key={category.uuid}
                  category={category}
                  toggleSelection={this.toggleSelection}
                  selection={map(currentSelection, "uuid")} />)}
            {showError && <ErrorText><FormattedMessage id="account.customisation.interests.errorMessage" /></ErrorText>}
            <Flex marginTop={1}>
              <Button
                primary
                disabled={!!saving || showError || !hasChanged}
                onClick={this.saveChanges}>
                <FormattedMessage id="account.customisation.interests.saveLabel" />
              </Button>
              <Button
                marginLeft={2}
                disabled={!!saving || currentSelection.length === 0}
                onClick={this.resetChanges}>
                <FormattedMessage id="account.customisation.interests.resetLabel" />
              </Button>
            </Flex>
            {saved && (
              <SuccessText marginTop={1}>
                <FormattedMessage
                  id="account.customisation.interests.saved"
                  values={{
                    link
                  }} />
              </SuccessText>)}
          </Loading>
        </Card>
        {unavailableCategories?.length > 0 && showUnavailableCategoryModal && (
          <UnavailableCategoryModal
            unavailableCategories={unavailableCategories}
            hideModal={this.hideModal} />
        )}
        <OnLeaveCustomisation block={hasChanged} trackLeave={this.abandonChanges} />
      </>
    )
  }
}

export default injectIntl(withAuth(CategoriesCustomisation))
